import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Login from './components/Login';
import HomePage from './components/HomePage';
import ProfileCompletion from './components/ProfileCompletion';
import ProfileManagement from './components/ProfileManagement';
import ErrorBoundary from './components/ErrorBoundary';
import NetworkStatusIndicator from './components/NetworkStatusIndicator';
import DigipinDevelopersPage from './components/developers/DigipinDevelopersPage';
import DigipinApiKeysAdminPage from './components/admin/DigipinApiKeysAdminPage';
import DigipinEncoderLandingPage from './components/digipin/DigipinEncoderLandingPage';
import DigipinEncoderIssuesPage from './components/digipin/DigipinEncoderIssuesPage';
import ClipToCADLandingPage from './components/cliptocad/ClipToCADLandingPage';
import ClipToCADIssuesPage from './components/cliptocad/ClipToCADIssuesPage';
import QgisPluginsPage from './components/plugins/QgisPluginsPage';
import TermsOfService from './components/legal/TermsOfService';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import PublicMapView from './components/PublicMapView';
import PricingPage from './components/PricingPage';
import UserAccountPage from './components/UserAccountPage';
import AdminHubPage from './components/admin/AdminHubPage';
import { useAppSelector, useAppDispatch } from './store/hooks';
import {
  restoreSessionStart,
  restoreSessionSuccess,
  restoreSessionFailure,
} from './store/slices/authSlice';
import { fetchLayers } from './store/slices/layersSlice';
import { restoreSession } from './utils/sessionManager';
import { claimGuestWorkspace } from './services/guestWorkspaceService';
import { remapPendingShareLayer } from './services/shareMapService';
import { trackEvent } from './services/analyticsService';
import { API_ORIGIN } from './config/api';
import { useGeoveraTheme } from './theme/ThemeContext';
import { SeoRoute } from './seo';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isRestoring } = useAppSelector((state) => state.auth);
  const location = useLocation();

  // Show loading while restoring session
  if (isRestoring) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const redirect = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return <>{children}</>;
};

import WebGISWorkspace from './components/WebGISWorkspace';

/**
 * Dashboard — always the full WebGIS shell (AppShell + live map).
 * Simple mode is a guided overlay on the same map (Phase 3 unified shell).
 */
const Dashboard = () => {
  return <WebGISWorkspace />;
};

function App() {
  const dispatch = useAppDispatch();
  const { isRestoring } = useAppSelector((state) => state.auth);
  const { palette } = useGeoveraTheme();

  // Restore session on app mount (same-origin API in production)
  useEffect(() => {
    const initializeSession = async () => {
      dispatch(restoreSessionStart());

      try {
        const sessionData = await restoreSession(API_ORIGIN);

        if (sessionData) {
          // Session restored successfully
          dispatch(
            restoreSessionSuccess({
              user: {
                id: sessionData.user.id,
                username: sessionData.user.email.split('@')[0],
                email: sessionData.user.email,
                role: sessionData.user.role || 'user',
              },
              token: '',
            })
          );
          console.log('Session restored successfully for user:', sessionData.user.email);

          // Claim guest snapshot (if any), then load cloud layers
          try {
            const claim = await claimGuestWorkspace();
            if (claim.success) remapPendingShareLayer(claim.layers);
            if (claim.claimedLayers > 0) {
              console.log('[guest] claimed layers on restore:', claim.claimedLayers);
              trackEvent('guest_claim', { claimedLayers: claim.claimedLayers });
            }
          } catch (claimErr) {
            console.warn('[guest] claim on restore failed', claimErr);
          }
          dispatch(fetchLayers());
        } else {
          // No valid session found
          dispatch(restoreSessionFailure());
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
        dispatch(restoreSessionFailure());
      }
    };

    initializeSession();
  }, [dispatch]);

  // Show loading screen while restoring session
  if (isRestoring) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: palette.bg.base,
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <SeoRoute />
        <AnalyticsRouteTracker />
        <>
          <Routes>
            {/* Public homepage — geovera.in root */}
            <Route path="/" element={<HomePage />} />

            {/* QGIS Plugins hub — DIGIPIN + ClipToCAD */}
            <Route path="/qgis-plugins" element={<QgisPluginsPage />} />
            <Route path="/plugins" element={<Navigate to="/qgis-plugins" replace />} />
            <Route path="/qgis" element={<Navigate to="/qgis-plugins" replace />} />

            {/* DIGIPIN Encoder Pro (QGIS) — product detail + issues */}
            <Route path="/digipin-encoder" element={<DigipinEncoderLandingPage />} />
            <Route path="/digipin-encoder/issues" element={<DigipinEncoderIssuesPage />} />
            <Route path="/digipin" element={<Navigate to="/digipin-encoder" replace />} />

            {/* ClipToCAD (QGIS plugin) — product detail + issues */}
            <Route path="/cliptocad" element={<ClipToCADLandingPage />} />
            <Route path="/cliptocad/issues" element={<ClipToCADIssuesPage />} />
            <Route path="/cadtozip" element={<Navigate to="/cliptocad" replace />} />
            <Route path="/cad-to-zip" element={<Navigate to="/cliptocad" replace />} />

            {/* Public map viewer route for viral sharing */}
            <Route path="/m/:layerId" element={<PublicMapView />} />

            {/* Public Routes — sign in / sign up (same Google OAuth page) */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login />} />
            <Route path="/sign-up" element={<Login />} />
            <Route path="/signin" element={<Login />} />
            <Route path="/sign-in" element={<Login />} />

            {/* Pricing + free forever tiers */}
            <Route path="/pricing" element={<PricingPage />} />

            {/* Guest / free map workspace — no login required (Adoption Spine viral loop) */}
            <Route path="/map" element={<Dashboard />} />
            <Route path="/map/simple" element={<Dashboard />} />

            {/* Legal Routes */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <UserAccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/complete"
              element={
                <ProtectedRoute>
                  <ProfileCompletion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfileManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developers/digipin"
              element={
                <ProtectedRoute>
                  <DigipinDevelopersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/api-keys"
              element={
                <ProtectedRoute>
                  <DigipinApiKeysAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Unknown routes → homepage (not login) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </>
      </Router>
      <NetworkStatusIndicator />
    </ErrorBoundary>
  );
}

export default App;
